import { formatDate } from "@/shared/formatters/dateTimeFormatters";
import dayjs, { DateInput } from "@/utils/date";

// The individual date/time *component* options that Intl.DateTimeFormat
// rejects when combined with `dateStyle`/`timeStyle`. Everything else
// (`timeZone`, `hourCycle`, `hour12`, `calendar`, `numberingSystem`,
// `localeMatcher`, `formatMatcher`) is valid alongside the styles and must be
// passed through rather than silently dropped.
const COMPONENT_KEYS: (keyof Intl.DateTimeFormatOptions)[] = [
  "weekday",
  "era",
  "year",
  "month",
  "day",
  "dayPeriod",
  "hour",
  "minute",
  "second",
  "fractionalSecondDigits",
  "timeZoneName",
];

function normalizeOptions(options?: Intl.DateTimeFormatOptions) {
  if (!options) {
    return undefined;
  }
  if (options.dateStyle || options.timeStyle) {
    const conflicting = COMPONENT_KEYS.filter((key) => options[key] != null);
    if (conflicting.length === 0) {
      return options;
    }
    // eslint-disable-next-line no-console
    console.warn(
      `Invalid options passed to FormatDate: ${conflicting.join(", ")}. These date/time component options can't be combined with dateStyle or timeStyle and were dropped.`,
    );
    const cleaned = { ...options };
    for (const key of conflicting) {
      delete cleaned[key];
    }
    return cleaned;
  }
  return options;
}

type FormatTimeProps = {
  date: NonNullable<DateInput>;
  options?: Intl.DateTimeFormatOptions;
} & React.ComponentProps<"span">;

export function FormatDate({
  date: dateProp,
  options: optionsProp,
  ...spanProps
}: FormatTimeProps) {
  const date = dayjs(dateProp);

  let formatOptions: Intl.DateTimeFormatOptions | undefined =
    normalizeOptions(optionsProp);

  if (!formatOptions) {
    const now = dayjs();
    formatOptions = {
      day: "numeric",
      month: "short",
      year: now.isSame(date, "year") ? undefined : "numeric",
    };
  }

  return <span {...spanProps}>{formatDate(date, formatOptions)}</span>;
}

export function FormatDateRange({
  start,
  end,
}: Omit<FormatTimeProps, "date"> & {
  start: NonNullable<DateInput>;
  end: NonNullable<DateInput>;
}) {
  let formatOptions: Intl.DateTimeFormatOptions | undefined = {
    day: "numeric",
    month: "short",
  };

  const currentYear = dayjs().year();
  const startYear = dayjs(start).year();
  const endYear = dayjs(end).year();
  const showYear =
    currentYear !== startYear ||
    currentYear !== endYear ||
    startYear !== endYear;

  if (showYear) {
    formatOptions.year = "numeric";
  }

  return (
    <>
      <FormatDate date={start} options={formatOptions} /> -{" "}
      <FormatDate date={end} options={formatOptions} />
    </>
  );
}
